<c-container fluid>
  <!-- Stats -->
  @if (stats(); as s) {
    <c-row class="mb-3">
      <c-col xs="6" md="3" class="mb-3">
        <c-card class="stat-card">
          <c-card-body>
            <div class="stat-value">{{ s.total }}</div>
            <div class="stat-label">{{ 'card_entitlements.stat_total' | translate }}</div>
          </c-card-body>
        </c-card>
      </c-col>
      <c-col xs="6" md="3" class="mb-3">
        <c-card class="stat-card">
          <c-card-body>
            <div class="stat-value text-success">{{ s.requested }}</div>
            <div class="stat-label">{{ 'card_entitlements.stat_requested' | translate }}</div>
          </c-card-body>
        </c-card>
      </c-col>
      <c-col xs="6" md="3" class="mb-3">
        <c-card class="stat-card">
          <c-card-body>
            <div class="stat-value">{{ s.not_requested }}</div>
            <div class="stat-label">{{ 'card_entitlements.stat_not_requested' | translate }}</div>
          </c-card-body>
        </c-card>
      </c-col>
      <c-col xs="6" md="3" class="mb-3">
        <c-card class="stat-card">
          <c-card-body>
            <div class="stat-value" [class.text-danger]="s.rejections_open > 0">{{ s.rejections_open }}</div>
            <div class="stat-label">{{ 'card_entitlements.stat_rejections' | translate }}</div>
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>

    @if (hasMissingEntitlements()) {
      <div class="alert alert-warning">
        <strong>{{ s.missing_entitlements }}</strong>
        {{ 'card_entitlements.missing_warning' | translate }}
      </div>
    }
    @if (!s.lifetime_grant_enabled) {
      <div class="alert alert-info py-2">
        {{ 'card_entitlements.lifetime_disabled' | translate }}
      </div>
    }
  }

  @if (actionMessage()) {
    <div class="alert alert-success py-2">
      {{ 'card_entitlements.sync_success' | translate }}: {{ actionMessage() }}
    </div>
  }
  @if (actionError()) {
    <div class="alert alert-danger py-2">{{ actionError() }}</div>
  }

  <!-- Tabs -->
  <ul class="nav nav-tabs mb-0">
    <li class="nav-item">
      <button class="nav-link" [class.active]="tab() === 'entitlements'" (click)="selectTab('entitlements')">
        {{ 'card_entitlements.tab_entitlements' | translate }}
      </button>
    </li>
    <li class="nav-item">
      <button class="nav-link" [class.active]="tab() === 'rejections'" (click)="selectTab('rejections')">
        {{ 'card_entitlements.tab_rejections' | translate }}
        @if (stats(); as s) {
          @if (s.rejections_open > 0) {
            <c-badge color="danger" class="ms-1">{{ s.rejections_open }}</c-badge>
          }
        }
      </button>
    </li>
  </ul>

  <!-- ── Entitlements ─────────────────────────────────────────── -->
  @if (tab() === 'entitlements') {
    <c-card class="border-top-0">
      <c-card-header>
        <strong>{{ 'card_entitlements.title' | translate }}</strong>
      </c-card-header>
      <c-card-body>
        <c-row class="mb-3">
          <c-col xs="12" md="3">
            <label cLabel for="filterSearch">{{ 'card_entitlements.filter_search' | translate }}</label>
            <input
              cFormControl
              id="filterSearch"
              type="text"
              [ngModel]="filterSearch()"
              (ngModelChange)="filterSearch.set($event); applyFilters()"
              [placeholder]="'card_entitlements.search_placeholder' | translate"
            />
          </c-col>

          <c-col xs="12" md="2">
            <label cLabel for="filterStatus">{{ 'card_entitlements.filter_status' | translate }}</label>
            <select id="filterStatus" cSelect [ngModel]="filterStatus()" (ngModelChange)="filterStatus.set($event); applyFilters()">
              <option value="">{{ 'card_entitlements.all' | translate }}</option>
              @for (option of statusOptions; track option) {
                <option [value]="option">{{ 'card_entitlements.status_' + option | translate }}</option>
              }
            </select>
          </c-col>

          <c-col xs="12" md="2">
            <label cLabel for="filterOrigin">{{ 'card_entitlements.filter_origin' | translate }}</label>
            <select id="filterOrigin" cSelect [ngModel]="filterOrigin()" (ngModelChange)="filterOrigin.set($event); applyFilters()">
              <option value="">{{ 'card_entitlements.all' | translate }}</option>
              @for (option of originOptions; track option) {
                <option [value]="option">{{ 'card_entitlements.origin_' + option | translate }}</option>
              }
            </select>
          </c-col>

          <c-col xs="12" md="2">
            <label cLabel for="filterRequested">{{ 'card_entitlements.filter_requested' | translate }}</label>
            <select id="filterRequested" cSelect [ngModel]="filterRequested()" (ngModelChange)="filterRequested.set($event); applyFilters()">
              <option value="">{{ 'card_entitlements.all' | translate }}</option>
              <option value="yes">{{ 'card_entitlements.requested_yes' | translate }}</option>
              <option value="no">{{ 'card_entitlements.requested_no' | translate }}</option>
            </select>
          </c-col>

          <c-col xs="12" md="3" class="d-flex align-items-end">
            <button cButton color="secondary" (click)="clearFilters()">
              {{ 'card_entitlements.clear_filters' | translate }}
            </button>
          </c-col>
        </c-row>

        @if (loading()) {
          <div class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ 'card_entitlements.loading' | translate }}</span>
            </div>
          </div>
        } @else {
          @if (entitlements().length > 0) {
            <div class="table-responsive">
              <table cTable hover striped>
                <thead>
                  <tr>
                    <th>{{ 'card_entitlements.col_entitlement' | translate }}</th>
                    <th>{{ 'card_entitlements.col_member' | translate }}</th>
                    <th>{{ 'card_entitlements.col_origin' | translate }}</th>
                    <th>{{ 'card_entitlements.col_status' | translate }}</th>
                    <th>{{ 'card_entitlements.col_partner_card' | translate }}</th>
                    <th>{{ 'card_entitlements.col_requested' | translate }}</th>
                    <th>{{ 'card_entitlements.col_shipped' | translate }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  @for (item of entitlements(); track item.id) {
                    <tr>
                      <td><code>{{ item.entitlement_id }}</code></td>
                      <td>
                        <div>{{ item.user_name || '–' }}</div>
                        <small class="text-body-secondary">{{ item.user_email }}</small>
                      </td>
                      <td>
                        <c-badge [color]="originColor(item.origin)">
                          {{ 'card_entitlements.origin_' + item.origin | translate }}
                        </c-badge>
                      </td>
                      <td>
                        <c-badge [color]="statusColor(item.status)">
                          {{ 'card_entitlements.status_' + item.status | translate }}
                        </c-badge>
                      </td>
                      <td>
                        @if (item.partner_card_ref) {
                          <code>{{ item.partner_card_ref }}</code>
                        } @else {
                          <span class="text-body-secondary">–</span>
                        }
                      </td>
                      <td>
                        @if (item.requested_by_partner) {
                          <div>{{ formatDate(item.partner_first_lookup_at) }}</div>
                          <small class="text-body-secondary">
                            {{ item.partner_lookup_count }}× · {{ 'card_entitlements.last' | translate }}
                            {{ formatDate(item.partner_last_lookup_at) }}
                          </small>
                        } @else {
                          <c-badge color="secondary">{{ 'card_entitlements.never_requested' | translate }}</c-badge>
                        }
                      </td>
                      <td>{{ formatDateOnly(item.shipped_at) }}</td>
                      <td class="text-end">
                        <button cButton color="primary" variant="outline" size="sm" (click)="openDetail(item)">
                          {{ 'card_entitlements.details' | translate }}
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <div class="text-center py-4">
              <p class="text-body-secondary">{{ 'card_entitlements.no_records' | translate }}</p>
            </div>
          }

          @if (totalPages() > 0) {
            <c-row class="mt-3 align-items-center">
              <c-col xs="12" md="6">
                {{ 'card_entitlements.showing' | translate }}
                {{ (page() - 1) * perPage() + 1 }}–{{ Math.min(page() * perPage(), totalCount()) }}
                {{ 'card_entitlements.of' | translate }} {{ totalCount() }}
              </c-col>
              <c-col xs="12" md="6">
                <div class="d-flex justify-content-end align-items-center gap-2">
                  <select cSelect [ngModel]="perPage()" (ngModelChange)="onPerPageChange($event)"
                          class="form-select form-select-sm" style="width: auto;">
                    <option [value]="25">25</option>
                    <option [value]="50">50</option>
                    <option [value]="100">100</option>
                  </select>
                  <nav>
                    <ul class="pagination pagination-sm mb-0">
                      <li class="page-item" [class.disabled]="page() === 1">
                        <button class="page-link" (click)="onPageChange(page() - 1)" [disabled]="page() === 1">‹</button>
                      </li>
                      <li class="page-item disabled"><span class="page-link">{{ page() }} / {{ totalPages() }}</span></li>
                      <li class="page-item" [class.disabled]="page() === totalPages()">
                        <button class="page-link" (click)="onPageChange(page() + 1)" [disabled]="page() === totalPages()">›</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </c-col>
            </c-row>
          }
        }
      </c-card-body>
    </c-card>
  }

  <!-- ── Rejections ───────────────────────────────────────────── -->
  @if (tab() === 'rejections') {
    <c-card class="border-top-0">
      <c-card-header>
        <strong>{{ 'card_entitlements.rejections_title' | translate }}</strong>
        <div class="small text-body-secondary mt-1">{{ 'card_entitlements.rejections_hint' | translate }}</div>
      </c-card-header>
      <c-card-body>
        <c-row class="mb-3">
          <c-col xs="12" md="3">
            <label cLabel for="filterReason">{{ 'card_entitlements.filter_reason' | translate }}</label>
            <select id="filterReason" cSelect [ngModel]="filterReason()" (ngModelChange)="filterReason.set($event); applyFilters()">
              <option value="">{{ 'card_entitlements.all' | translate }}</option>
              @for (option of reasonOptions; track option) {
                <option [value]="option">{{ 'card_entitlements.reason_' + option | translate }}</option>
              }
            </select>
          </c-col>
          <c-col xs="12" md="4" class="d-flex align-items-end">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="filterOpenOnly"
                     [ngModel]="filterOpenOnly()" (ngModelChange)="filterOpenOnly.set($event); applyFilters()" />
              <label class="form-check-label" for="filterOpenOnly">
                {{ 'card_entitlements.only_unresolved' | translate }}
              </label>
            </div>
          </c-col>
        </c-row>

        @if (rejectionsLoading()) {
          <div class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ 'card_entitlements.loading' | translate }}</span>
            </div>
          </div>
        } @else {
          @if (rejections().length > 0) {
            <div class="table-responsive">
              <table cTable hover striped>
                <thead>
                  <tr>
                    <th>{{ 'card_entitlements.col_email' | translate }}</th>
                    <th>{{ 'card_entitlements.col_reason' | translate }}</th>
                    <th>{{ 'card_entitlements.col_attempts' | translate }}</th>
                    <th>{{ 'card_entitlements.col_first_attempt' | translate }}</th>
                    <th>{{ 'card_entitlements.col_last_attempt' | translate }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  @for (item of rejections(); track item.id) {
                    <tr [class.table-secondary]="item.resolved_at">
                      <td>
                        <div>{{ item.email }}</div>
                        @if (item.user_name) {
                          <small class="text-body-secondary">{{ item.user_name }} (#{{ item.user_id }})</small>
                        }
                      </td>
                      <td>
                        <c-badge [color]="reasonColor(item.reason)">
                          {{ 'card_entitlements.reason_' + item.reason | translate }}
                        </c-badge>
                        @if (item.resolved_at) {
                          <c-badge color="success" class="ms-1">{{ 'card_entitlements.resolved' | translate }}</c-badge>
                        }
                      </td>
                      <td>{{ item.attempts }}</td>
                      <td>{{ formatDate(item.first_attempt_at) }}</td>
                      <td>{{ formatDate(item.last_attempt_at) }}</td>
                      <td class="text-end">
                        @if (item.reason === 'no_entitlement' && !item.resolved_at) {
                          <button cButton color="primary" size="sm"
                                  [disabled]="syncingUserId() === item.user_id"
                                  (click)="grantCard(item)">
                            {{ 'card_entitlements.grant_card' | translate }}
                          </button>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <div class="text-center py-4">
              <p class="text-body-secondary">{{ 'card_entitlements.no_rejections' | translate }}</p>
            </div>
          }

          @if (rejectionsTotalPages() > 0) {
            <c-row class="mt-3 align-items-center">
              <c-col xs="12" md="6">
                {{ 'card_entitlements.showing' | translate }}
                {{ (rejectionsPage() - 1) * perPage() + 1 }}–{{ Math.min(rejectionsPage() * perPage(), rejectionsTotalCount()) }}
                {{ 'card_entitlements.of' | translate }} {{ rejectionsTotalCount() }}
              </c-col>
              <c-col xs="12" md="6">
                <div class="d-flex justify-content-end">
                  <nav>
                    <ul class="pagination pagination-sm mb-0">
                      <li class="page-item" [class.disabled]="rejectionsPage() === 1">
                        <button class="page-link" (click)="onRejectionsPageChange(rejectionsPage() - 1)"
                                [disabled]="rejectionsPage() === 1">‹</button>
                      </li>
                      <li class="page-item disabled">
                        <span class="page-link">{{ rejectionsPage() }} / {{ rejectionsTotalPages() }}</span>
                      </li>
                      <li class="page-item" [class.disabled]="rejectionsPage() === rejectionsTotalPages()">
                        <button class="page-link" (click)="onRejectionsPageChange(rejectionsPage() + 1)"
                                [disabled]="rejectionsPage() === rejectionsTotalPages()">›</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </c-col>
            </c-row>
          }
        }
      </c-card-body>
    </c-card>
  }
</c-container>

<!-- ── Detail modal ─────────────────────────────────────────────── -->
<c-modal [visible]="showDetail()" (visibleChange)="onDetailVisibleChange($event)" size="xl" [keyboard]="true">
  <c-modal-header>
    <h5 cModalTitle>
      {{ 'card_entitlements.detail_title' | translate }}
      @if (detail(); as d) { <code class="ms-2">{{ d.entitlement.entitlement_id }}</code> }
    </h5>
    <button cButtonClose (click)="closeDetail()"></button>
  </c-modal-header>

  <c-modal-body>
    @if (detailLoading()) {
      <div class="text-center py-4">
        <div class="spinner-border text-primary" role="status"></div>
      </div>
    }

    @if (detail(); as d) {
      <c-row class="mb-3">
        <c-col md="6">
          <p class="mb-1"><strong>{{ 'card_entitlements.col_member' | translate }}:</strong>
            {{ d.entitlement.user_name || '–' }} ({{ d.entitlement.user_email }})</p>
          <p class="mb-1"><strong>{{ 'card_entitlements.col_origin' | translate }}:</strong>
            {{ 'card_entitlements.origin_' + d.entitlement.origin | translate }}
            <span class="text-body-secondary">({{ d.entitlement.source_type }}:{{ d.entitlement.source_ref }})</span></p>
          <p class="mb-1"><strong>{{ 'card_entitlements.col_status' | translate }}:</strong>
            {{ 'card_entitlements.status_' + d.entitlement.status | translate }}</p>
          <p class="mb-1"><strong>{{ 'card_entitlements.granted_at' | translate }}:</strong>
            {{ formatDate(d.entitlement.granted_at) }}</p>
        </c-col>
        <c-col md="6">
          <p class="mb-1"><strong>{{ 'card_entitlements.col_partner_card' | translate }}:</strong>
            {{ d.entitlement.partner_card_ref || '–' }}
            @if (d.entitlement.card_last4) { <span class="text-body-secondary">•••• {{ d.entitlement.card_last4 }}</span> }</p>
          <p class="mb-1"><strong>{{ 'card_entitlements.expires' | translate }}:</strong>
            {{ formatDateOnly(d.entitlement.card_expires_on) }}</p>
          <p class="mb-1"><strong>{{ 'card_entitlements.tracking' | translate }}:</strong>
            {{ d.entitlement.carrier || '–' }} {{ d.entitlement.tracking_number || '' }}</p>
          <p class="mb-1"><strong>{{ 'card_entitlements.col_requested' | translate }}:</strong>
            @if (d.entitlement.requested_by_partner) {
              {{ formatDate(d.entitlement.partner_first_lookup_at) }} ({{ d.entitlement.partner_lookup_count }}×)
            } @else {
              {{ 'card_entitlements.never_requested' | translate }}
            }
          </p>
        </c-col>
      </c-row>

      <h6>{{ 'card_entitlements.events' | translate }}</h6>
      @if (d.events.length > 0) {
        <div class="table-responsive mb-3">
          <table cTable small>
            <thead>
              <tr>
                <th>{{ 'card_entitlements.col_event' | translate }}</th>
                <th>{{ 'card_entitlements.col_occurred' | translate }}</th>
                <th>{{ 'card_entitlements.col_partner_card' | translate }}</th>
                <th>{{ 'card_entitlements.col_reason' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (event of d.events; track event.id) {
                <tr>
                  <td><c-badge color="info">{{ event.event }}</c-badge></td>
                  <td>{{ formatDate(event.occurred_at) }}</td>
                  <td>{{ event.partner_card_ref || '–' }}</td>
                  <td>{{ event.reason || '–' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <p class="text-body-secondary">{{ 'card_entitlements.no_events' | translate }}</p>
      }

      <h6>{{ 'card_entitlements.transactions' | translate }}</h6>
      @if (d.transactions.length > 0) {
        <div class="table-responsive mb-3">
          <table cTable small>
            <thead>
              <tr>
                <th>{{ 'card_entitlements.col_occurred' | translate }}</th>
                <th>{{ 'card_entitlements.col_type' | translate }}</th>
                <th>{{ 'card_entitlements.col_status' | translate }}</th>
                <th>{{ 'card_entitlements.col_merchant' | translate }}</th>
                <th class="text-end">{{ 'card_entitlements.col_amount' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (tx of d.transactions; track tx.id) {
                <tr>
                  <td>{{ formatDate(tx.occurred_at) }}</td>
                  <td>{{ tx.transaction_type }}</td>
                  <td>
                    {{ tx.status }}
                    @if (tx.decline_reason) { <small class="text-danger d-block">{{ tx.decline_reason }}</small> }
                  </td>
                  <td>{{ tx.merchant_name || '–' }}</td>
                  <td class="text-end">{{ formatAmount(tx.amount, tx.currency) }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <p class="text-body-secondary">{{ 'card_entitlements.no_transactions' | translate }}</p>
      }

      <h6>{{ 'card_entitlements.topups' | translate }}</h6>
      @if (d.topups.length > 0) {
        <div class="table-responsive">
          <table cTable small>
            <thead>
              <tr>
                <th>{{ 'card_entitlements.col_occurred' | translate }}</th>
                <th>{{ 'card_entitlements.col_status' | translate }}</th>
                <th>{{ 'card_entitlements.col_source' | translate }}</th>
                <th class="text-end">{{ 'card_entitlements.col_amount' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (topup of d.topups; track topup.id) {
                <tr>
                  <td>{{ formatDate(topup.occurred_at) }}</td>
                  <td>{{ topup.status }}</td>
                  <td>{{ topup.source || '–' }}</td>
                  <td class="text-end">{{ formatAmount(topup.amount, topup.currency) }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <p class="text-body-secondary">{{ 'card_entitlements.no_topups' | translate }}</p>
      }
    }
  </c-modal-body>

  <c-modal-footer>
    <button cButton color="secondary" (click)="closeDetail()">{{ 'card_entitlements.close' | translate }}</button>
  </c-modal-footer>
</c-modal>
